import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  AnomalyStats,
  ClassificationStats,
  CustomerReport,
  CustomerReports,
  ProductReport,
  StatsWindow,
} from '@visionqc/shared';
import { TASKS } from '@visionqc/shared';
import { api } from '../api/client';
import { CustomerPill } from '../components/CustomerPill';

const WINDOWS: Array<{ value: StatsWindow; label: string }> = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
];

/**
 * Every customer on one page, with their products underneath.
 *
 * The dashboard answers "how is the line running" and needs a customer picked
 * first. This answers "which customer is not doing well", which the dashboard
 * cannot: finding it there means changing the dropdown once per customer and
 * remembering the numbers in between.
 *
 * NO COMBINED HEADLINE, AT ANY LEVEL. A pass rate and an agreement rate have no
 * common unit, and a customer running both would get a figure that moves when
 * the mix of work changes rather than when the line does. That is the mistake
 * the dashboard made in a smaller way — good over a total spanning both tasks —
 * and it read 34.5% where the truth was 44.1%. Two columns, always.
 */
export function ReportingView() {
  const [window, setWindow] = useState<StatsWindow>('24h');
  const [data, setData] = useState<CustomerReports | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());

  const load = useCallback(() => {
    setError(null);
    api
      .customerReports(window)
      .then(setData)
      .catch((e: Error) => {
        setData(null);
        setError(e.message);
      });
  }, [window]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const toggle = (id: number) =>
    setOpen((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div>
      <h1 className="view-title">Reporting</h1>

      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="row">
          <div>
            <label>Time window</label>
            <select value={window} onChange={(e) => setWindow(e.target.value as StatsWindow)}>
              {WINDOWS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)' }}>{error}</div>}

      {!data ? (
        !error && <div className="panel spinner">Loading…</div>
      ) : data.customers.length === 0 ? (
        <div className="panel muted">No customers yet.</div>
      ) : (
        <div className="grid" style={{ gap: 14 }}>
          {data.customers.map((c) => (
            <CustomerCard
              key={c.customerId ?? 'unassigned'}
              report={c}
              open={open.has(c.customerId ?? -1)}
              onToggle={() => toggle(c.customerId ?? -1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerCard({
  report,
  open,
  onToggle,
}: {
  report: CustomerReport;
  open: boolean;
  onToggle: () => void;
}) {
  const navigate = useNavigate();
  const quiet = !report.anomaly && !report.classification;
  const unassigned = report.customerId === null;

  return (
    <div
      className="panel"
      style={
        unassigned
          ? // Marked, not hidden. These products hold most of this
            // installation's inspections and belong to nobody; a report that
            // dropped them would have looked complete and been wrong by more
            // than it was right.
            { borderColor: 'var(--warn)' }
          : undefined
      }
    >
      <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
        <button
          className="row"
          onClick={onToggle}
          style={{
            gap: 10,
            background: 'none',
            border: 'none',
            color: 'var(--text)',
            cursor: 'pointer',
            padding: 0,
            fontSize: 18,
            fontWeight: 700,
            textAlign: 'left',
          }}
        >
          <span aria-hidden style={{ fontSize: 12, opacity: 0.7, width: 10 }}>
            {open ? '▾' : '▸'}
          </span>
          <CustomerPill name={report.customerName} hue={report.customerHue} />
          <span className="muted" style={{ fontSize: 13, fontWeight: 500 }}>
            {report.products.length === 1 ? '1 product' : `${report.products.length} products`}
          </span>
        </button>

        {!unassigned && report.customerId !== null && (
          <button className="btn sm" onClick={() => navigate(`/dashboard?customer=${report.customerId}`)}>
            Open dashboard →
          </button>
        )}
      </div>

      {unassigned && (
        <div className="muted" style={{ fontSize: 12, marginTop: 6, color: 'var(--warn)' }}>
          These products have no customer, so they appear in no customer's report. Assign them on
          the product to move their figures where they belong.
        </div>
      )}

      {quiet ? (
        <div className="muted" style={{ fontSize: 13, marginTop: 10 }}>
          Nothing ran in this window.
        </div>
      ) : (
        <div
          className="grid"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginTop: 12 }}
        >
          {report.anomaly && <QualitySummary stats={report.anomaly} />}
          {report.classification && <ClassificationSummary stats={report.classification} />}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          {report.products.length === 0 ? (
            <div className="muted" style={{ fontSize: 13 }}>
              No products.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Asks</th>
                  <th>Result</th>
                  <th>Inspections</th>
                  <th>Needs a person</th>
                </tr>
              </thead>
              <tbody>
                {report.products.map((p) => (
                  <ProductRow key={p.productId} product={p} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function ProductRow({ product }: { product: ProductReport }) {
  const a = product.anomaly;
  const c = product.classification;
  const ran = (a?.total ?? 0) + (c?.total ?? 0);

  /*
    The result column carries different meanings per row, so it always carries
    its unit with it. "44.1% pass" and "98.0% agreement" in the same column
    without the word after them would invite exactly the comparison that has no
    meaning.
  */
  let result = '—';
  if (a && a.total > 0) result = `${(a.goodRatio * 100).toFixed(1)}% pass`;
  else if (c && c.total > 0) {
    const reviewed = c.predicted - c.unreviewed;
    result = reviewed > 0 ? `${(((reviewed - c.corrected) / reviewed) * 100).toFixed(1)}% agreement` : 'not reviewed';
  }

  // What a person still owes this product: labels on one side, confirmations on
  // the other. Different work, but the same question — is anyone keeping up?
  const owed = a ? a.unannotated : (c?.unreviewed ?? 0) + (c?.rejected ?? 0);

  return (
    <tr style={{ cursor: 'default' }}>
      <td>{product.productName}</td>
      <td className="muted" style={{ fontSize: 13 }}>
        {TASKS[product.task].question}
      </td>
      <td style={{ fontFamily: 'var(--mono)' }}>{result}</td>
      <td style={{ fontFamily: 'var(--mono)' }}>{ran.toLocaleString()}</td>
      <td style={{ fontFamily: 'var(--mono)', color: owed > 0 ? 'var(--warn)' : undefined }}>
        {owed > 0 ? owed.toLocaleString() : '—'}
      </td>
    </tr>
  );
}

function QualitySummary({ stats }: { stats: AnomalyStats }) {
  return (
    <Block
      title={TASKS.anomaly.label}
      headline={`${(stats.goodRatio * 100).toFixed(1)}%`}
      headlineLabel="pass rate"
      tone="var(--good)"
      rows={[
        ['Inspected', stats.total.toLocaleString()],
        ['Rejected', stats.bad.toLocaleString()],
        ['Unsure', stats.unsure.toLocaleString()],
        ['Awaiting a label', stats.unannotated.toLocaleString()],
      ]}
    />
  );
}

function ClassificationSummary({ stats }: { stats: ClassificationStats }) {
  const reviewed = stats.predicted - stats.unreviewed;
  const agreement = reviewed > 0 ? ((reviewed - stats.corrected) / reviewed) * 100 : null;

  return (
    <Block
      title={TASKS.classification.label}
      // Null reads "—", never 100%. Dividing by an empty review set would
      // flatter an unreviewed model into a perfect score.
      headline={agreement === null ? '—' : `${agreement.toFixed(1)}%`}
      headlineLabel={agreement === null ? 'nothing reviewed yet' : 'agreement'}
      tone={agreement === null ? 'var(--text-dim)' : 'var(--good)'}
      rows={[
        ['Classified', stats.predicted.toLocaleString()],
        ['Sent to a person', stats.rejected.toLocaleString()],
        ['Corrected', stats.corrected.toLocaleString()],
        ['Awaiting confirmation', stats.unreviewed.toLocaleString()],
      ]}
    />
  );
}

function Block({
  title,
  headline,
  headlineLabel,
  tone,
  rows,
}: {
  title: string;
  headline: string;
  headlineLabel: string;
  tone: string;
  rows: Array<[string, string]>;
}) {
  return (
    <div style={{ background: 'var(--bg-panel-2)', borderRadius: 'var(--radius)', padding: 16 }}>
      <div className="stat-label">{title}</div>
      <div className="row" style={{ gap: 10, alignItems: 'baseline', marginTop: 4 }}>
        <span style={{ fontSize: 34, fontWeight: 800, color: tone, fontVariantNumeric: 'tabular-nums' }}>
          {headline}
        </span>
        <span className="muted" style={{ fontSize: 13 }}>
          {headlineLabel}
        </span>
      </div>
      <div className="grid" style={{ gap: 4, marginTop: 12 }}>
        {rows.map(([k, v]) => (
          <div key={k} className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
            <span className="muted" style={{ fontSize: 13 }}>
              {k}
            </span>
            <strong style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{v}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}
